import { useField } from 'formik'
import { BrandPlanTacticContents, ContentCount } from 'types'
import { reportError } from 'utils/error'
import { useMemo } from 'react'
import { UsePostTypeFieldProps } from 'screens/Schedule/EditChannelsModal/Types'

export const usePostTypeField = ({ name, postType, accountId, contentType }: UsePostTypeFieldProps) => {
  const [field] = useField<BrandPlanTacticContents>(name)

  const index = useMemo(() => {
    const matchesContentType = (v: ContentCount) => (contentType ? v.content_type === contentType : !v.content_type)

    const matchesAccount = (v: ContentCount) => {
      if (accountId === undefined) return true
      return v.account_ids?.includes(accountId) ?? false
    }

    const noAccountIds = (v: ContentCount) => !v.account_ids || v.account_ids.length === 0

    const find = (pred: (v: ContentCount) => boolean) => field.value.findIndex((v) => v.type === postType && pred(v))

    // Primary: exact match on contentType + account
    let result = find((v) => matchesContentType(v) && matchesAccount(v))

    // Fallback 1: old data where content_type was never set
    if (result === -1 && !contentType) {
      result = find(matchesAccount)
    }

    // Fallback 2: single account — entry has no account_ids array
    if (result === -1 && accountId !== undefined) {
      result = find((v) => matchesContentType(v) && noAccountIds(v))
    }

    // Fallback 3: old data + single account
    if (result === -1 && !contentType && accountId !== undefined) {
      result = find(noAccountIds)
    }

    if (result === -1) {
      reportError(`No entry found for postType=${postType}, accountId=${accountId}. Entries should be initialized.`, {
        severity: 'error',
      })
    }

    return result
  }, [field.value, accountId, postType, contentType])

  return useField<ContentCount | undefined>(`${name}.${index}`)
}
